import type { SessionCreationReason } from '../../common/services/types';

// Mirrors ContractHistoryController’s default number of root groups per page.
export const CONTRACT_HISTORY_PAGE_SIZE = 20;

// Contract-owned wire models. Missing audit data never implies complete history.
export interface ContractAuditDto {
    readonly recordInformation?: ContractRecordInformationDto | null;
    readonly isHistoryComplete?: boolean;
    readonly historyCount: number;
}

export interface ContractHistoryPageDto {
    readonly changes: readonly ContractChangeGroupDto[];
    readonly total: number;
    readonly throughSequence: number;
    readonly page: number;
    readonly pageSize: number;
}

export interface ContractRecordInformationDto {
    readonly created?: ContractActorTimestampDto | null;
    readonly lastSaved?: ContractActorTimestampDto | null;
    readonly createdFrom?: ContractPredecessorDto | null;
}

export interface ContractActorTimestampDto {
    readonly userId?: string | null;
    readonly at?: string | null;
}

export interface ContractPredecessorDto {
    readonly sessionId: string;
    readonly createdAt?: string | null;
    readonly reason?: SessionCreationReason | null;
}

export interface ContractChangeGroupDto {
    readonly id: string;
    readonly saveId: string;
    readonly userId?: string | null;
    readonly completedAt?: string | null;
    readonly change: ContractChangeDto;
    readonly automaticChanges?: readonly ContractAutomaticChangeDto[] | null;
}

export interface ContractAutomaticChangeDto {
    readonly change: ContractChangeDto;
    readonly triggeredBy: ContractHistoryTargetDto;
}

export interface ContractChangeDto {
    readonly target: ContractHistoryTargetDto;
    readonly oldValue: ContractHistoryValueDto;
    readonly newValue: ContractHistoryValueDto;
}

export interface ContractHistoryTargetDto {
    readonly reference: ContractChangeReferenceDto;
    readonly kind: 'Unknown' | 'Field' | 'RepeatedRecord' | 'Layer' | 'Section' | 'Structure';
    readonly label: string;
    readonly context?: readonly string[] | null;
}

export interface ContractHistoryValueDto {
    readonly kind: 'Unknown' | 'Value' | 'NotSet' | 'NotPresent' | 'Removed';
    readonly text?: string | null;
}

// Reserved RuntimeDataEntry.Value JSON string. These are causal hints only;
// the server authenticates actors/times and computes the actual saved changes.
export const contractHistoryIntentRuntimeKey = 'ContractHistoryIntent';

export interface ContractSaveIntentDto {
    readonly saveId: string;
    readonly directChanges: readonly ContractDirectChangeIntentDto[];
}

export interface ContractDirectChangeIntentDto {
    readonly id: string;
    readonly target: ContractChangeReferenceDto;
    readonly automaticChanges: readonly ContractAutomaticChangeIntentDto[];
}

export interface ContractAutomaticChangeIntentDto {
    readonly target: ContractChangeReferenceDto;
    readonly triggeredBy: ContractChangeReferenceDto;
}

export interface ContractChangeReferenceDto {
    readonly nodeId?: string | null;
    readonly itemId: string;
    readonly layerId?: string | null;
    readonly sectionId?: string | null;
    readonly recordId?: string | null;
}
